import { RouteManager, TranslationProvider } from "@/legacy";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ErrorBoundary } from "react-error-boundary";
import { QueryClientProvider } from "react-query";
import { PersistGate } from "redux-persist/integration/react";

import { ImmiNotifications } from "@/common/notifications";
import { AsyncLayout } from "@/layouts/AsyncRoute";
import CenteredLayout from "@/layouts/CenteredLayout";
import DecisivelyLayout from "@/layouts/DecisivelyLayout";
import ScopedDecisivelyLayout from "@/layouts/ScopedDecisivelyLayout";
import { ErrorFallbackPage } from "@/pages/Error";

import { config } from "@/config/env";
import { global } from "@/global";
import { globalStore } from "@/store";
import { AppThemeProvider } from "@/theme";
import { i18nProvider } from "./providers";
import { ThemeProvider } from "./providers/ThemeContext";
// global imports
import routes from "./routes";

import { VariablesProvider } from "@/common/variables";
import { EncloseSetValueFromTestCasesCtxComp, WithTestCasesRootCtx } from "@/components/TestCasesTree/RootCtx";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { DismissGuardRouterProvider } from "@imminently/decisively-ui/DismissGuard";
import { PersistentComponents } from "@imminently/decisively-ui/PersistentComponents";
import AppToasts from "./AppToasts";
import AppVersionToasts from "./AppVersionToasts";

import { FeatureGuard } from "@/components/FeatureGuard";
import { CommandPalette, CommandPaletteContextProvider } from "@/features/CommandPalette";
import { TenancyRecoveryProvider } from "@/features/tenancy";
import { ConnectedAdapterWorkflowProvider } from "@/features/adapters";
import Icon from "@imminently/decisively-ui/Icon";
import SdkProvider from "@imminently/decisively-ui/SdkProvider";
import { ConnectedRouter } from "connected-react-router";
// TODO remove moment entirely
// import "moment/dist/locale/en-au";
import { useTranslation } from "react-i18next";
import { Provider } from "react-redux";

// import our global editor styles
import "@/common/editor/components/EditorStyles.scss";

const setFavicon = (src) => {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }
  link.href = src;
};

const setTitle = (title) => {
  document.title = title;
};

if (config.APP_TITLE) {
  setTitle(config.APP_TITLE);
}
if (config.APP_FAVICON) {
  setFavicon(config.APP_FAVICON);
}

const CanaryWatermark = () => {
  if (config.APP_BRANCH === "canary") {
    console.warn("🐤 Running in canary branch, expect instability and bugs.");
    return (
      <img
        src="/images/canary.png"
        alt="canary"
        className="fixed bottom-0 right-0 w-36 h-auto z-9999 opacity-50 pointer-events-none"
      />
    );
  }
  return null;
};

const layouts = {
  decisively: <DecisivelyLayout />,
  "decisively-scoped": <ScopedDecisivelyLayout />,
  async: <AsyncLayout />,
  centered: <CenteredLayout />,
};

export const AppProviders = ({ children }) => {
  const { t } = useTranslation();

  return (
    <ThemeProvider>
      <SdkProvider t={t}>
        <QueryClientProvider client={global.queryClient}>
          <DndProvider backend={HTML5Backend}>
            <WithTestCasesRootCtx>
              <Provider store={globalStore.store}>
                <ConnectedRouter history={globalStore.history}>
                  <DismissGuardRouterProvider
                    block={(blocker) => globalStore.history.block(blocker as any)}
                    push={(location) => globalStore.history.push(location as any)}
                    replace={(location) => globalStore.history.replace(location as any)}
                    goBack={() => globalStore.history.goBack()}
                  >
                    <TranslationProvider i18nProvider={i18nProvider}>
                      <AppThemeProvider>
                        <CommandPaletteContextProvider shortcut="mod+k">
                          <TenancyRecoveryProvider>
                            <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
                              <PersistGate
                                loading={<LoadingSpinner size="sm" />}
                                persistor={globalStore.persistor}
                              >
                                <VariablesProvider>
                                  <ConnectedAdapterWorkflowProvider>{children}</ConnectedAdapterWorkflowProvider>
                                </VariablesProvider>
                                {/* @ts-ignore This is a web component that we inject */}
                                <rn-banner
                                  bottom="0"
                                  position="absolute"
                                />
                              </PersistGate>
                            </ErrorBoundary>
                            <CanaryWatermark />
                            <FeatureGuard feature="developer.command_palette">
                              <CommandPalette />
                            </FeatureGuard>
                          </TenancyRecoveryProvider>
                          {/* Ensure these are outside of the error boundary, so we can use them */}
                          <ImmiNotifications />
                          <AppToasts position="bottom-right" />
                          <AppVersionToasts />
                          <PersistentComponents layer={WORKSPACE_LAYER} />
                          <EncloseSetValueFromTestCasesCtxComp />
                        </CommandPaletteContextProvider>
                      </AppThemeProvider>
                    </TranslationProvider>
                  </DismissGuardRouterProvider>
                </ConnectedRouter>
              </Provider>
            </WithTestCasesRootCtx>
          </DndProvider>
        </QueryClientProvider>
      </SdkProvider>
    </ThemeProvider>
  );
};

export const App = () => {
  return (
    <AppProviders>
      <RouteManager
        forceLogin
        routes={routes}
        layouts={layouts}
        unauthenticatedDefault="/signin"
        invalidRoleRedirect="/forbidden"
      />
    </AppProviders>
  );
};
